import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import { recordPayment, updatePayment } from "../api/paymentsApi";

// Define prop types for the PaymentModal
interface PaymentModalProps {
  orderId: number;
  paymentId?: number; // For editing an existing payment
  initialData?: { amount: number; method: string; status: string };
  onClose: () => void;
  onPaymentSuccess: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  orderId,
  paymentId,
  initialData,
  onClose,
  onPaymentSuccess,
}) => {
  const [amount, setAmount] = useState<number | "">(initialData?.amount || "");
  const [method, setMethod] = useState<string>(initialData?.method || "");
  const [status, setStatus] = useState<string>(initialData?.status || "");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = async () => {
    if (!amount || !method || !status) {
      alert("All fields are required!");
      return;
    }

    try {
      if (paymentId) {
        // Update existing payment
        await updatePayment(paymentId, { amount, method, status });
      } else {
        // Add new payment
        await recordPayment({ orderId, amount, method, status });
      }
      onPaymentSuccess(); // Refresh order details
      onClose();
    } catch (error) {
      console.error("Error recording/updating payment:", error);
      alert("Failed to process payment. Please try again.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 2 : 4,
          padding: isMobile ? 1 : 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant={isMobile ? "h6" : "h5"} align="center">
          {paymentId ? "Edit Payment" : "Add Payment"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? 1 : 2,
          }}
        >
          {/* Amount Input */}
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || "")}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />

          {/* Payment Method Selector */}
          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <MenuItem value="Email Transfer">Email Transfer</MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Payment Status Selector */}
          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: isMobile ? "space-between" : "flex-end",
          gap: isMobile ? 1 : 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          fullWidth={isMobile}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          size={isMobile ? "small" : "medium"}
          fullWidth={isMobile}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
